import type { GqlClient } from '@/types/gqlClient'
import { AccountType, ProjectFileUsage } from 'src/utils/enums'

export type RawProjectFile = {
  project_id: number
  file_id: string
  use: ProjectFileUsage
  status: 'ACCEPTED' | 'REJECTED' | null
  comment: string | null
  account_visibility: string | null
  file: {
    id: string
    name: string
    mime_type: string
    size: number | null
    updated_at: string
    // Upload time. Drives the display order of the files inside a usage —
    // `updated_at` moves whenever a file is touched, so it is not a stable
    // reading order.
    created_at: string
  } | null
}

export type RawUrbanAnalysisFile = {
  urban_analysis_id: number
  file_id: string
  use: ProjectFileUsage
  status: 'ACCEPTED' | 'REJECTED' | null
  comment: string | null
  account_visibility: string | null
  file: RawProjectFile['file']
}

export type RawUrbanAnalysis = {
  // Who ordered the urban analysis. Drives salesman visibility of the
  // UA-derived town-planning documents (see useDocumentsView).
  requestor: AccountType | null
}

export type DocumentsViewPayload = {
  project_files: RawProjectFile[]
  urban_analysis_files: RawUrbanAnalysisFile[]
  // Optional so test fixtures and any pre-existing caller need not supply it;
  // the live query always returns it (empty array when no UA exists).
  urban_analysis?: RawUrbanAnalysis[]
  // The project's type — drives the project-type document opt-out (a POOL sheds
  // the drawing views, 3D insertion and existing survey). Optional so fixtures
  // need not supply it; the live query always returns it.
  project?: { project_type: string | null } | null
}

export const getDocumentsView = (
  gqlClient: GqlClient,
  projectID: number
): Promise<DocumentsViewPayload> => {
  const query = `
    query GetDocumentsView($project_id: Int!) {
      project_files(
        # Oldest upload first, matching getProjectFilesOfUsages. Without an
        # explicit order Hasura returns physical row order, so a file could
        # change places between two loads of the same page.
        order_by: { file: { created_at: asc } }
        where: {
          project_id: { _eq: $project_id }
          # Exclude only the unassigned inbox (use IS NULL). UNDEFINED is now a
          # real "Autres documents" category and must flow into the groups.
          use: { _is_null: false }
          file: { enabled: { _eq: true } }
        }
      ) {
        project_id
        file_id
        use
        status
        comment
        account_visibility
        file {
          id
          name
          mime_type
          size
          updated_at
          created_at
        }
      }
      urban_analysis_files(
        order_by: { file: { created_at: asc } }
        where: {
          urban_analysis: { project_id: { _eq: $project_id } }
          use: { _neq: UNDEFINED }
          file: { enabled: { _eq: true } }
        }
      ) {
        urban_analysis_id
        file_id
        use
        status
        comment
        account_visibility
        file {
          id
          name
          mime_type
          size
          updated_at
          created_at
        }
      }
      urban_analysis(
        where: { project_id: { _eq: $project_id }, enabled: { _eq: true } }
        limit: 1
      ) {
        requestor
      }
      project: projects_by_pk(id: $project_id) {
        project_type
      }
    }
  `
  return gqlClient
    .request(query, { project_id: projectID })
    .then((data: unknown) => data as DocumentsViewPayload)
}

export type BulkAssignResult = {
  affected_rows: number
  returning: Array<{ project_id: number; file_id: string; use: ProjectFileUsage }>
}

export const bulkAssignProjectFiles = (
  gqlClient: GqlClient,
  projectID: number,
  fileIds: string[],
  usage: ProjectFileUsage
): Promise<BulkAssignResult> => {
  const mutation = `
    mutation BulkAssignProjectFiles(
      $project_id: Int!
      $file_ids: [uuid!]!
      $usage: project_files_uses_enum!
    ) {
      update_project_files(
        where: {
          project_id: { _eq: $project_id }
          file_id: { _in: $file_ids }
          # The to-sort inbox is files with no usage yet (use IS NULL) — see
          # listProjectFilesToSort. The #393 split made UNDEFINED a real
          # category ("Autres documents"), so guarding on UNDEFINED here matched
          # zero inbox rows and the assignment silently no-op'd. Guard on NULL so
          # only still-unassigned files are updated (idempotent on re-assign).
          use: { _is_null: true }
        }
        _set: { use: $usage }
      ) {
        affected_rows
        returning {
          project_id
          file_id
          use
        }
      }
    }
  `
  return gqlClient
    .request(mutation, {
      project_id: projectID,
      file_ids: fileIds,
      usage,
    })
    .then((data: unknown) => {
      const result = data as { update_project_files: BulkAssignResult }
      return result.update_project_files
    })
}
